// Review queue — everything that named a physician but couldn't be linked
// with confidence: the clean-up's ambiguous rows, and every intake or
// referral where a parent typed a doctor we don't have. A coordinator
// resolves each one: link to an existing record, create a new one (the
// registry fills it), or dismiss.
//
// Two item shapes:
//   source_table = clients / intake_submissions / leads / dme_* — "who is
//     this?": resolving links the source row to a physician.
//   source_table = physicians — a question about a directory record itself
//     (a fax conflict, or several NPPES candidates): resolving updates it.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { listReviewQueue, resolveReview, dismissReview, listPhysicians, listFacilities, fmtPhone, CARE_ROLE_LABEL } from '../../lib/directory';
import { RefCode, Pill, Chip, Empty, Modal, label } from './ui';
import PhysicianPicker from './PhysicianPicker';
import PhysicianForm from './PhysicianForm';

const SOURCE_LABEL = { clients: 'Client record', intake_submissions: 'Intake form', leads: 'Referral', dme_orders: 'DME order', dme_cmns: 'CMN', cmn_records: 'CMN', physicians: 'Directory record' };
const SOURCE_PATH  = { clients: id => `/clients/${id}`, leads: id => `/leads/${id}` };

export default function ReviewQueueTab({ canEdit, onCountChange }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const actor = profile?.full_name || 'staff';
  const [items, setItems] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');
  const [active, setActive] = useState(null);      // item being resolved
  const [creating, setCreating] = useState(null);  // { item, query }
  const [err, setErr] = useState('');

  const [faxHistory, setFaxHistory] = useState({});   // fax number → most recent outbound attempt
  const load = async () => {
    const [{ data: q }, { data: p }, { data: f }] = await Promise.all([listReviewQueue('open'), listPhysicians(), listFacilities()]);
    setItems(q || []); setPhysicians((p || []).filter(x => x.status === 'active')); setFacilities(f || []); setLoading(false);
    onCountChange?.((q || []).length);
    // evidence for fax conflicts: has anything ever been delivered to each candidate?
    const nums = [...new Set((q || []).flatMap(i => Array.isArray(i.raw?.fax_candidates) ? i.raw.fax_candidates : []))];
    if (nums.length) {
      const { data: fx } = await supabase.from('faxes').select('recipient_fax, faxage_status, created_at').eq('direction', 'outbound').in('recipient_fax', nums).order('created_at', { ascending: false });
      const h = {};
      for (const r of fx || []) if (!h[r.recipient_fax]) h[r.recipient_fax] = { status: r.faxage_status, created_at: r.created_at };
      setFaxHistory(h);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const kinds = useMemo(() => ({
    all: items.length,
    surname: items.filter(i => /surname/i.test(i.reason)).length,
    several: items.filter(i => /Several physicians/i.test(i.reason)).length,
    fax: items.filter(i => /fax numbers/i.test(i.reason)).length,
    npi: items.filter(i => /NPPES has several|check digit|not in the NPPES/i.test(i.reason)).length,
    form: items.filter(i => /intake|referral|Typed on a form/i.test(i.reason) || ['intake_submissions', 'leads'].includes(i.source_table)).length,
  }), [items]);
  const visible = items.filter(i =>
    kind === 'all' ? true
    : kind === 'surname' ? /surname/i.test(i.reason)
    : kind === 'several' ? /Several physicians/i.test(i.reason)
    : kind === 'fax' ? /fax numbers/i.test(i.reason)
    : kind === 'npi' ? /NPPES has several|check digit|not in the NPPES/i.test(i.reason)
    : (/intake|referral|Typed on a form/i.test(i.reason) || ['intake_submissions', 'leads'].includes(i.source_table)));

  const link = async (item, physician, status = 'linked') => {
    setErr('');
    const { error } = await resolveReview(item, physician.id, actor, status);
    if (error) { setErr(error.message); return; }
    setActive(null); load();
  };
  const dismiss = async (item) => {
    const note = window.prompt('Dismiss this item — why? (optional)') ?? null;
    if (note === null) return;
    await dismissReview(item, actor, note);
    setActive(null); load();
  };

  // directory-record questions: fax conflict / NPI choice
  const setFax = async (item, fax) => {
    await supabase.from('physicians').update({ fax, updated_by: actor, notes: null }).eq('id', item.physician_id);
    await resolveReview(item, item.physician_id, actor, 'linked', `Fax set to ${fmtPhone(fax)}`);
    load();
  };
  const setNpi = async (item, s) => {
    const { error } = await supabase.from('physicians').update({ npi: s.npi, updated_by: actor }).eq('id', item.physician_id);
    if (error) { setErr(/duplicate|unique/i.test(error.message) ? `NPI ${s.npi} already belongs to another record — merge the two from the Physicians tab instead.` : error.message); return; }
    await resolveReview(item, item.physician_id, actor, 'linked', `NPI set to ${s.npi} from NPPES`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <Chip on={kind === 'all'} onClick={() => setKind('all')}>All <b>{kinds.all}</b></Chip>
        <Chip on={kind === 'surname'} onClick={() => setKind('surname')}>Surname only <b>{kinds.surname}</b></Chip>
        <Chip on={kind === 'several'} onClick={() => setKind('several')}>Several in one field <b>{kinds.several}</b></Chip>
        <Chip on={kind === 'npi'} onClick={() => setKind('npi')}>Pick an NPI <b>{kinds.npi}</b></Chip>
        <Chip on={kind === 'fax'} onClick={() => setKind('fax')}>Fax conflicts <b>{kinds.fax}</b></Chip>
        <Chip on={kind === 'form'} onClick={() => setKind('form')}>From forms <b>{kinds.form}</b></Chip>
      </div>
      {err && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--danger)' }}>{err}</div>}
      {loading ? <Empty><div className="loading-spinner" style={{ margin: '0 auto' }} /></Empty>
        : visible.length === 0 ? <Empty>Nothing waiting. Every physician on record is linked.</Empty>
        : (
        <div style={{ display: 'grid', gap: 10 }}>
          {visible.map(item => {
            const raw = item.raw || {};
            const isRecord = item.source_table === 'physicians';
            const path = SOURCE_PATH[item.source_table];
            return (
              <div key={item.id} className="card" style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 12, alignItems: 'start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                    <Pill tone="neutral">{SOURCE_LABEL[item.source_table] || item.source_table}</Pill>
                    {item.source_role && <Pill tone="copper">{CARE_ROLE_LABEL[item.source_role] || item.source_role}</Pill>}
                    <b style={{ cursor: path ? 'pointer' : 'default', color: path ? 'var(--accent)' : 'inherit' }} onClick={() => path && navigate(path(item.source_id))}>{item.source_label}</b>
                  </div>
                  {!isRecord && (
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, background: 'var(--bg2)', padding: '6px 10px', borderRadius: 6, margin: '8px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {raw.name}{raw.npi ? `  · NPI ${raw.npi}` : ''}{raw.practice ? `\n${raw.practice}` : ''}{raw.phone ? `  · tel ${raw.phone}` : ''}{raw.fax ? `  · fax ${raw.fax}` : ''}{raw.address ? `\n${raw.address}` : ''}
                    </div>
                  )}
                  {isRecord && (() => { const doc = physicians.find(p => p.id === item.physician_id); return doc ? (
                    <div style={{ fontSize: 12.5, color: 'var(--text3)', margin: '6px 0' }}>
                      On record as <RefCode code={doc.ref_code} /> {doc.specialty || 'no specialty'} · {doc.facility_name || 'no facility'}{doc.city ? ` · ${doc.city}` : ''}{doc.phone ? ` · tel ${fmtPhone(doc.phone)}` : ''}
                    </div>) : null; })()}
                  <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>{item.reason}</div>

                  {/* fax conflict on a directory record */}
                  {isRecord && Array.isArray(raw.fax_candidates) && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      {raw.fax_candidates.map(f => {
                        const h = faxHistory[f];
                        return (
                          <div key={f} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {canEdit ? <button className="btn btn-outline btn-sm" onClick={() => setFax(item, f)}>Use {fmtPhone(f)}</button> : <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }}>{fmtPhone(f)}</span>}
                            <span style={{ fontSize: 11, color: h ? (h.status === 'Success' ? 'var(--success)' : h.status ? 'var(--danger)' : 'var(--warn)') : 'var(--text3)' }}>
                              {h ? `last fax ${new Date(h.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} · ${h.status === 'Success' ? 'delivered' : h.status ? h.status.toLowerCase() : 'pending'}` : 'never faxed'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* NPI choice on a directory record */}
                  {isRecord && !raw.fax_candidates && (item.suggestions || []).length > 0 && canEdit && (
                    <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
                      {item.suggestions.map(s => (
                        <div key={s.npi} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setNpi(item, s)}>This one</button><span>{s.display}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* suggestions for a "who is this?" item */}
                  {!isRecord && (item.suggestions || []).length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>Could be:</span>
                      {item.suggestions.slice(0, 4).map((s, i) => {
                        const doc = s.physician_id ? physicians.find(p => p.id === s.physician_id) : null;
                        if (doc && canEdit) return <button key={i} className="btn btn-outline btn-sm" onClick={() => link(item, doc)}><RefCode code={doc.ref_code} />&nbsp;{doc.display_name}</button>;
                        return <Pill key={i} tone="neutral">{s.display}</Pill>;
                      })}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }}>
                    {!isRecord && <button className="btn btn-primary btn-sm" onClick={() => { setActive(item); setErr(''); }}>Link to…</button>}
                    {!isRecord && <button className="btn btn-outline btn-sm" onClick={() => setCreating({ item, query: raw.name || '' })}>+ Create new</button>}
                    <button className="btn btn-outline btn-sm" style={{ color: 'var(--text3)' }} onClick={() => dismiss(item)}>Dismiss</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {active && (
        <Modal onClose={() => setActive(null)} width={560} title={<>Link <span style={{ fontWeight: 400 }}>“{(active.raw || {}).name}”</span> to</>}>
          <div style={{ marginTop: 12 }}>
            <div style={label}>{active.source_label} · {CARE_ROLE_LABEL[active.source_role] || active.source_role || 'physician'}</div>
            <PhysicianPicker physicians={physicians} value={null} autoFocus onChange={(p) => p && link(active, p)}
              onAddNew={(q) => { setCreating({ item: active, query: q || (active.raw || {}).name || '' }); setActive(null); }} />
            {err && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{err}</div>}
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10 }}>Tip: the typed text is kept on the source record whatever you pick — only the link is added.</div>
          </div>
        </Modal>
      )}
      {creating && (
        <PhysicianForm initialQuery={creating.query} facilities={facilities}
          onSaved={(doc) => { const item = creating.item; setCreating(null); link(item, { ...doc, display_name: `${doc.last_name}, ${doc.first_name}` }, 'created'); }}
          onClose={() => setCreating(null)} />
      )}
    </div>
  );
}
